
import React from "react";
import { Calendar, FileText, MessageSquare, Settings, Users, Clock, Heart, Video, CreditCard } from "lucide-react";
import { useScrollReveal } from "@/hooks/useScrollReveal";

const FeatureCards: React.FC = () => {
  const { ref: titleRef, isVisible: titleVisible } = useScrollReveal();
  const { ref: cardsRef, isVisible: cardsVisible } = useScrollReveal({ threshold: 0.2 });
  
  const features = [
    {
      icon: <Video className="h-10 w-10 text-white" />,
      title: "HiveShare - Safe Video Sharing",
      description: "Share class videos securely without YouTube risks. Parents love it, kids stay safe, you control everything.",
      color: "bg-gradient-to-br from-[#F75C57] to-[#f98f8b]"
    },
    {
      icon: <Calendar className="h-10 w-10 text-white" />,
      title: "Smart Scheduling System",
      description: "Streamline class management, enrollment tracking, and waitlist handling in one intuitive interface.",
      color: "bg-gradient-to-br from-[#FA8C00] to-[#FBA94C]"
    }, 
    {
      icon: <CreditCard className="h-10 w-10 text-white" />,
      title: "Bill Assist - Get Paid Faster",
      description: "Never chase payments again. AI-powered billing intelligence that knows which families to prioritize.",
      color: "bg-gradient-to-br from-[#4ba3c7] to-[#7dbdd9]"
    }, 
    {
      icon: <MessageSquare className="h-10 w-10 text-white" />,
      title: "Parent Portal - Save 15+ Hours Weekly",
      description: "Parents handle makeups, payments, and updates themselves. Your 24/7 assistant that never sleeps.",
      color: "bg-gradient-to-br from-[#7ad4a0] to-[#a5e4c0]"
    }
  ];
  
  return (
    <div className="relative z-10 -mt-10 mb-24">
      <div className="max-w-7xl mx-auto px-4 py-[67px]">
        <div 
          ref={titleRef}
          className={`text-center mb-8 md:mb-12 px-4 sm:px-0 transition-all duration-1000 ${
            titleVisible 
              ? 'opacity-100 translate-y-0' 
              : 'opacity-0 translate-y-8'
          }`}
        >
          <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-800 mb-3 md:mb-4">
            A complete platform built for activity businesses
          </h2>
          <p className="text-base sm:text-lg text-gray-600 leading-relaxed max-w-2xl mx-auto">
            Everything you need to run your program efficiently in one place
          </p>
        </div>
        
        <div 
          ref={cardsRef}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8 px-4 sm:px-0"
        >
          {features.map((item, index) => (
            <div 
              key={index} 
              className={`rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 hover:scale-[1.02] group cursor-pointer ${
                cardsVisible 
                  ? 'opacity-100 translate-y-0' 
                  : 'opacity-0 translate-y-12'
              }`}
              style={{ 
                animationDelay: `${index * 150}ms`,
                transitionDelay: cardsVisible ? `${index * 200}ms` : '0ms'
              }}
            >
              <div className={`${item.color} p-6 flex items-center justify-center relative overflow-hidden`}>
                {/* Animated background overlay */}
                <div className="absolute inset-0 bg-white/10 translate-x-full group-hover:translate-x-0 transition-transform duration-700"></div>
                
                <div className="h-16 w-16 flex items-center justify-center bg-white/20 rounded-xl backdrop-blur-sm relative z-10 transition-all duration-300 group-hover:scale-110 group-hover:rotate-3 group-hover:bg-white/30">
                  <div className="transition-transform duration-300 group-hover:scale-110">
                    {item.icon}
                  </div>
                </div>
              </div>
              <div className="p-6 bg-white group-hover:bg-gray-50 transition-colors duration-300">
                <h3 className="text-xl font-bold mb-3 text-gray-800 transition-colors duration-300 group-hover:text-gray-900">{item.title}</h3>
                <p className="text-gray-600 transition-colors duration-300 group-hover:text-gray-700 leading-relaxed">{item.description}</p>
              </div>
              
              {/* Subtle border animation */}
              <div className="absolute inset-0 rounded-2xl border-2 border-transparent group-hover:border-white/20 transition-colors duration-300"></div>
            </div>
          ))}
        </div>
        
        {/* CTA Button to Features Page */}
        <div className="text-center mt-12 px-4 sm:px-0">
          <button 
            onClick={() => window.location.href = '/features'}
            className="bg-primary hover:bg-primary/90 text-white font-semibold px-8 py-4 rounded-xl text-lg transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl"
          >
            Explore All Features
          </button>
          <p className="text-gray-600 mt-3 text-sm">
            See every feature that makes Hivetivity complete
          </p>
        </div>
      </div>
    </div>
  );
};

export default FeatureCards;
